import { formatUnitValue, convertUnitValue, unitPreference, unitSymbol } from "./units.js";
import { renderWaterColumnProfile } from "./water-column-profile.js";
import { currentProfileHtml } from "./current-profile.js";
import { setHtml } from "./html.js";
import { formatAppDateTime } from "./app-time.js";

export function formatBrowserDataTime(value) {
  return formatAppDateTime(value, { timeOptions: { timeZoneName: "short" } });
}

function svgNode(document, tag, attributes = {}, text = "") {
  const node = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  node.textContent = text;
  return node;
}

export function renderColumnChart(container, rows, kind, bottom, thermocline = null) {
  const document = container.ownerDocument;
  const data = rows.filter((r) => Number.isFinite(r.depth) && r.depth >= 0 && r.depth <= bottom
    && (kind === "temperature" ? Number.isFinite(r.temperature) && r.temperature >= -5 && r.temperature <= 45
      : Number.isFinite(r.speed) && r.speed >= 0));
  container.replaceChildren();
  if (!data.length || !Number.isFinite(bottom) || bottom <= 0) return;
  const svg = svgNode(document, "svg", { viewBox: "0 0 640 450", role: "img", "aria-label": `${kind} versus depth, depth increases downward` });
  const output = document.createElement("output");
  output.setAttribute("aria-live", "polite");
  output.textContent = "Hover, tap or focus a model level for its reading.";
  const value = (row) => kind === "temperature"
    ? convertUnitValue(row.temperature, "C", unitPreference("waterTemperature"))
    : convertUnitValue(row.speed * 3.6, "kph", unitPreference("speed"));
  const values = data.map(value);
  let min = kind === "temperature" ? Math.floor(Math.min(...values) - 1) : 0;
  let max = Math.max(...values) + (kind === "temperature" ? 1 : .1);
  if (max <= min) max = min + 1;
  const x = (n) => 80 + (n - min) / (max - min) * 510;
  const y = (depth) => 40 + depth / bottom * 350;
  for (let i = 0; i <= 5; i += 1) {
    const d = i * bottom / 5, xx = 80 + i * 102;
    svg.append(svgNode(document, "line", { x1: 80, x2: 590, y1: y(d), y2: y(d), class: "chart-grid" }));
    svg.append(svgNode(document, "text", { x: 70, y: y(d) + 5, "text-anchor": "end" }, String(Number(convertUnitValue(d, "m", unitPreference("depth")).toFixed(0)))));
    svg.append(svgNode(document, "text", { x: xx, y: 415, "text-anchor": "middle" }, ((min + (max - min) * i / 5).toFixed(1))));
  }
  svg.append(svgNode(document, "text", { x: 335, y: 440, "text-anchor": "middle" }, `${kind === "temperature" ? "Temperature" : "Current speed"} (${unitSymbol(kind === "temperature" ? "waterTemperature" : "speed")})`));
  svg.append(svgNode(document, "text", { x: 80, y: 22 }, `Depth (${unitSymbol("depth")}) — increases downward`));
  if (kind === "temperature" && Number.isFinite(thermocline) && thermocline <= bottom && thermocline >= 0) {
    svg.append(svgNode(document, "line", { x1: 80, x2: 590, y1: y(thermocline), y2: y(thermocline), class: "chart-thermocline" }));
  }
  for (const row of data) {
    const xx = x(value(row)), yy = y(row.depth);
    const reading = `${formatUnitValue(row.depth, "depth", "m", { decimals: 0 })}: ${kind === "temperature"
      ? formatUnitValue(row.temperature, "waterTemperature", "C")
      : `${formatUnitValue(row.speed * 3.6, "speed", "kph")}, ${Number.isFinite(row.direction) ? `${row.direction.toFixed(0)}° toward` : "calm; direction undefined"}`}`;
    if (kind === "currents" && Number.isFinite(row.direction) && row.speed > .001) {
      const angle = row.direction * Math.PI / 180, dx = Math.sin(angle) * 13, dy = -Math.cos(angle) * 13;
      const tipX = xx + dx, tipY = yy + dy;
      svg.append(svgNode(document, "path", { class: "chart-vector", d: `M${xx - dx},${yy - dy} L${tipX},${tipY} M${tipX - dx * .4 - dy * .25},${tipY - dy * .4 + dx * .25} L${tipX},${tipY} L${tipX - dx * .4 + dy * .25},${tipY - dy * .4 - dx * .25}` }));
    }
    const dot = svgNode(document, "circle", { cx: xx, cy: yy, r: 5, tabindex: 0, class: "chart-point", "aria-label": reading });
    dot.append(svgNode(document, "title", {}, reading));
    for (const event of ["pointerenter", "focus", "click"]) dot.addEventListener(event, () => { output.textContent = reading; });
    svg.append(dot);
  }
  container.append(svg, output);
}

export function startConditions() {
  document.querySelectorAll("time[data-browser-time][datetime]").forEach((element) => {
    const formatted = formatBrowserDataTime(element.dateTime);
    if (formatted) element.textContent = formatted;
  });
  const search = document.getElementById("port-search");
  if (search) {
    const filter = () => {
      const query = search.value.toLocaleLowerCase().trim();
      let count = 0;
      document.querySelectorAll("[data-port-search]").forEach((li) => {
        li.hidden = !li.dataset.portSearch.includes(query);
        if (!li.hidden) count += 1;
      });
      document.querySelectorAll("[data-port-group]").forEach((section) => {
        section.hidden = ![...section.querySelectorAll("[data-port-search]")].some((li) => !li.hidden);
      });
      document.getElementById("port-count").textContent = `${count} matching locations`;
    };
    search.addEventListener("input", filter);
    filter();
  }
  document.querySelectorAll("[data-water-column-profile]").forEach((container) => {
    const payload = container.nextElementSibling;
    if (!payload?.matches("script[type='application/json'][data-water-column-profile-json]")) return;
    try {
      renderWaterColumnProfile(container, JSON.parse(payload.textContent));
    } catch {
      container.textContent = "The water-column profile could not be displayed.";
    }
  });
  document.querySelectorAll("[data-current-profile]").forEach((container) => {
    const payload = container.nextElementSibling;
    if (!payload?.matches("script[type='application/json'][data-current-profile-json]")) return;
    try {
      setHtml(container, currentProfileHtml(JSON.parse(payload.textContent), 0));
    } catch {
      container.textContent = "The current profile could not be displayed.";
    }
  });
  const update = () => {
    document.querySelectorAll("[data-delta-celsius]").forEach((el) => {
      const unit = unitPreference("waterTemperature");
      const delta = convertUnitValue(Number(el.dataset.deltaCelsius), "C", unit) - convertUnitValue(0, "C", unit);
      el.textContent = `${delta.toFixed(1)} ${unitSymbol("waterTemperature")}`;
    });
    document.querySelectorAll("[data-value][data-unit][data-key]").forEach((el) => {
      const { value, unit, key } = el.dataset;
      el.textContent = key === "direction" && el.dataset.directionLabel
        ? el.dataset.directionLabel
        : ["direction", "period"].includes(key) ? `${Number(value).toFixed(0)} ${unit === "deg" ? "°" : unit}` : formatUnitValue(Number(value), key, unit, { decimals: key === "depth" ? 0 : 1 });
    });
    document.querySelectorAll("[data-profile]").forEach((container) => {
      const kind = container.dataset.profile;
      const rows = [...document.querySelectorAll(`[data-profile-table="${kind}"] tbody tr`)].map((tr) => ({
        depth: Number(tr.dataset.depth), temperature: Number(tr.dataset.temperature), speed: Number(tr.dataset.speed),
        direction: tr.dataset.direction ? Number(tr.dataset.direction) : null
      }));
      renderColumnChart(container, rows, kind, Number(container.dataset.bottom), container.dataset.thermocline ? Number(container.dataset.thermocline) : null);
    });
  };
  document.addEventListener("glc-units-changed", update);
  update();
}
